import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  FormHelperText,
  Grid,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { AccountType, Currency, CreateAccountRequest } from '../types/api';
import { accountsService } from '../services/accounts-service';

interface AddAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  name: string;
  description: string;
  account_type: AccountType;
  currency: Currency;
  institution: string;
  account_number: string;
  initial_balance: string;
  /** Date the value was assessed. A hand-entered figure goes stale silently. */
  valued_at: string;
  /** Where the figure came from, so it can be checked later. */
  valuation_note: string;
  /** Vehicle identification number, when the asset is a vehicle. */
  vin: string;
}

interface FormErrors {
  name?: string;
  account_type?: string;
  currency?: string;
  initial_balance?: string;
  vin?: string;
  general?: string;
}

const ACCOUNT_TYPES: { value: AccountType; label: string; description: string }[] = [
  { value: 'Asset', label: 'Asset', description: 'Checking, savings, cash accounts' },
  { value: 'Investment', label: 'Investment', description: 'Stocks, bonds, mutual funds, ETFs' },
  { value: 'Debt', label: 'Debt', description: 'Credit cards, loans, mortgages' },
];

// Every currency the server's validCurrencies map accepts. A code missing
// here cannot be picked, which is indistinguishable from the server refusing
// it -- and is why INR accounts could not be created.
const CURRENCIES: { value: Currency; label: string }[] = [
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
];

/** Symbol shown in the balance field. '$' is the fallback, not a default. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  CAD: '$',
  USD: '$',
  AUD: '$',
  EUR: '\u20AC',
  GBP: '\u00A3',
  JPY: '\u00A5',
  CNY: '\u00A5',
  CHF: 'Fr',
  INR: '\u20B9',
};

export const AddAccountDialog: React.FC<AddAccountDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    account_type: 'Asset',
    currency: 'CAD',
    institution: '',
    account_number: '',
    initial_balance: '0.00',
    valued_at: new Date().toISOString().slice(0, 10),
    valuation_note: '',
    vin: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }
    
    if (!formData.account_type) {
      newErrors.account_type = 'Account type is required';
    }
    
    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    }

    // A VIN is 17 characters and excludes I, O and Q so they cannot be read as
    // 1 and 0. Catching it here means a transposed entry surfaces while the
    // owner is still looking at the car, not months later against an insurance
    // record.
    const vin = formData.vin.trim().toUpperCase();
    if (vin && !/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
      newErrors.vin = 'A VIN is 17 characters and cannot contain I, O or Q';
    }

    // Validate initial balance is a valid number
    const balance = parseFloat(formData.initial_balance);
    if (isNaN(balance)) {
      newErrors.initial_balance = 'Initial balance must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const accountData: CreateAccountRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        account_type: formData.account_type,
        currency: formData.currency,
        institution: formData.institution.trim() || undefined,
        account_number: formData.account_number.trim() || undefined,
        external_id: undefined,
        metadata: {},
      };

      const response = await accountsService.createAccount(accountData);

      // Post the opening value the user typed.
      //
      // This previously only console.log'd it, so adding a house worth
      // $1.75M created an account showing $0 -- the value silently vanished
      // between the form and the ledger, and nothing reported a failure.
      //
      // A DEBT is entered as a positive number ("I owe $40,600") because that
      // is how anyone reads a loan statement, and stored negative, because a
      // debt that loses its sign becomes an asset. The form does the flip so
      // the ledger's convention never has to be explained.
      const initialBalance = parseFloat(formData.initial_balance);
      const accountId = (response as any)?.data?.id ?? (response as any)?.id;
      if (!isNaN(initialBalance) && initialBalance !== 0 && accountId) {
        const signed = formData.account_type === 'Debt'
          ? -Math.abs(initialBalance)
          : initialBalance;
        await accountsService.setValuation(accountId, {
          value: String(signed),
          valued_at: formData.valued_at || undefined,
          note: formData.valuation_note.trim() || undefined,
          vin: formData.vin.trim() || undefined,
        });
      }

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error('Failed to create account:', error);
      setErrors({
        general: error.message || 'Failed to create account. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    
    setFormData({
      name: '',
      description: '',
      account_type: 'Asset',
      currency: 'CAD',
      institution: '',
      account_number: '',
      initial_balance: '0.00',
      valued_at: new Date().toISOString().slice(0, 10),
      valuation_note: '',
      vin: '',
    });
    setErrors({});
    onClose();
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(2);
  };

  const handleBalanceBlur = () => {
    const formatted = formatCurrency(formData.initial_balance);
    setFormData(prev => ({ ...prev, initial_balance: formatted }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-account-dialog-title"
    >
      <DialogTitle id="add-account-dialog-title">
        Add New Account
      </DialogTitle>
      
      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Account Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name || 'e.g., "TD Checking Account"'}
                required
                disabled={isSubmitting}
              />
            </Grid>

            {/* Account Type */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Account Type"
                value={formData.account_type}
                onChange={handleInputChange('account_type')}
                error={!!errors.account_type}
                helperText={errors.account_type}
                required
                disabled={isSubmitting}
              >
                {ACCOUNT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Box sx={{ fontWeight: 'medium' }}>{type.label}</Box>
                      <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        {type.description}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Currency */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Currency"
                value={formData.currency}
                onChange={handleInputChange('currency')}
                error={!!errors.currency}
                helperText={errors.currency}
                required
                disabled={isSubmitting}
              >
                {CURRENCIES.map((currency) => (
                  <MenuItem key={currency.value} value={currency.value}>
                    {currency.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Initial Balance */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Initial Balance"
                type="number"
                value={formData.initial_balance}
                onChange={handleInputChange('initial_balance')}
                onBlur={handleBalanceBlur}
                error={!!errors.initial_balance}
                helperText={
                  errors.initial_balance ||
                  (formData.account_type === 'Debt'
                    // Asked for as a positive number and stored negative. Every
                    // loan statement reads "you owe $40,600", so asking for a
                    // minus sign invites the one typo that turns a debt into an
                    // asset and moves net worth by twice the balance.
                    ? 'How much you owe, as a positive number'
                    : 'Current account balance')
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {CURRENCY_SYMBOLS[formData.currency] ?? '$'}
                    </InputAdornment>
                  ),
                }}
                disabled={isSubmitting}
              />
            </Grid>

            {/* Valuation date. Only meaningful for a hand-entered value, which
                is exactly what this dialog creates -- a synced account restates
                its own balance and never goes stale. */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Value as of"
                type="date"
                value={formData.valued_at}
                onChange={handleInputChange('valued_at')}
                InputLabelProps={{ shrink: true }}
                helperText="When this value was assessed"
                disabled={isSubmitting}
              />
            </Grid>

            {/* Provenance. A valuation nobody can trace cannot be defended or
                rechecked a year later, when it matters most. */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Where the value came from"
                value={formData.valuation_note}
                onChange={handleInputChange('valuation_note')}
                placeholder="HouseSigma Aug 2026 · dealer trade-in quote"
                helperText="Optional, but it is what makes the figure checkable later"
                disabled={isSubmitting}
              />
            </Grid>

            {/* VIN. Shown for any account, because a vehicle can be entered as
                the asset or as the loan against it, and either is a reasonable
                place to record which car it is. */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VIN (vehicles)"
                value={formData.vin}
                onChange={handleInputChange('vin')}
                inputProps={{ maxLength: 17, style: { textTransform: 'uppercase' } }}
                error={!!errors.vin}
                helperText={errors.vin || '17 characters, no I, O or Q'}
                disabled={isSubmitting}
              />
            </Grid>

            {/* Institution */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Institution"
                value={formData.institution}
                onChange={handleInputChange('institution')}
                helperText="Bank or financial institution name"
                disabled={isSubmitting}
              />
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.account_number}
                onChange={handleInputChange('account_number')}
                helperText="Optional. Encrypted at rest; only the last 4 are ever shown."
                disabled={isSubmitting}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                helperText="Optional notes about this account"
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Creating...' : 'Create Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};